import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Progress} from 'antd';
import {ReactComponent as MasterCardLogo} from '../../static/images/pages/cards/mastercard-logo.svg';
import {ReactComponent as WithLessWhiteLogo} from '../../static/images/pages/cards/withless-logo-white.svg';
import {ReactComponent as WithLessDarkLogo} from '../../static/images/pages/cards/withless-logo-dark.svg';
import frozenImg from '../../static/images/pages/cards/frozen.png';
import {cardsHelpers, objectHelpers} from '../../utils/helpers';
import {
  StyledCreditCard,
  StyledCreditCardDetails,
  StyledCreditCardOwner,
  StyledCreditCardNumber,
  StyledCreditCardFooter,
  StyledCreditCardCVVCode,
  StyledCreditCardTopAlert,
  StyledCreditCardBottomAlert,
  StyledCreditCardBlockedWindow,
  StyledCreditCardContainer,
  StyledCreditCardLimit,
  StyledCreditCardLimitHelper,
  StyledCreditCardErrorMessage,
  StyledCreditCardFrozenBackground,
  StyledCreditCardFrozenMessage,
} from './StyledCreditCard';
import {cardTypesConstants} from '../../constants';


const gOP = objectHelpers.getObjProp;

const getFormattedCardNumber = (cardNumber, isPlaceholder) => {
  if (!cardNumber || isPlaceholder) {
    return '**** **** **** ****'.split(' ').map((chunk, index) => (
      <span key={index}>{chunk}</span>
    ));
  }

  const maskedNumber = cardNumber.replace(/.(?=.{4})/g, '*');
  const formatted = cardsHelpers.getFormattedCardNumber(maskedNumber);

  return formatted.split(' ').map((chunk, index) => (
    <span
      key={index}
      className={index === 3 && chunk !== '****' ? 'top' : ''}
    >
      {chunk}
    </span>
  ));
};

const CreditCard = ({
  actions,
  cardData,
  isFrozen,
  isOnHold = false,
  isTerminated = false,
  hasNoId = false,
  error = false,
  errorMessage,
  limits,
  showPlaceholder = false,
  variant = cardTypesConstants.SUBSCRIPTION,
  ...rest
}) => {
  const [t] = useTranslation(['cards', 'main']);

  const enableLimit = useMemo(() => !isTerminated && !error, [error, isTerminated]);

  const CardLogo = useMemo(() => variant === cardTypesConstants.DIGITAL ? WithLessDarkLogo : WithLessWhiteLogo, [variant]);

  const {limit, percent, used} = useMemo(() => cardsHelpers.getCardLimitDetails(limits), [limits]);

  const cardErrorState = useMemo(() => {
    const errorConditions = [
      { condition: error, message: t('errors.cardError') },
      { condition: isTerminated, message: t('statuses.terminated') },
      { condition: hasNoId, message: t('errors.noId') },
      { condition: isOnHold, message: t('errors.hold') },
    ];

    const found = errorConditions.find(({ condition }) => condition);
    if (found) {
      return { hasError: true, message: found.message };
    }

    return { hasError: false, message: errorMessage };
  }, [errorMessage, error, isTerminated, hasNoId, isOnHold, t]);

  const isPlaceholder = useMemo(() => showPlaceholder || hasNoId || isOnHold, [showPlaceholder, hasNoId, isOnHold]);

  const shouldHideCardDetails = useMemo(() => (cardErrorState.hasError && !hasNoId && !isOnHold) || error, [cardErrorState, hasNoId, isOnHold, error]);

  const cardNumber = useMemo(() => getFormattedCardNumber(gOP(cardData, 'number'), isPlaceholder), [cardData, isPlaceholder]);

  const className = enableLimit ? 'enable-limit' : '';

  const cardOwnerAndNumber = (
    <>
      <StyledCreditCardOwner>{cardData.embossedName}</StyledCreditCardOwner>
      <StyledCreditCardNumber>{cardNumber}</StyledCreditCardNumber>
    </>
  );

  return (
    <StyledCreditCardContainer {...rest}>
      <StyledCreditCard
        className={className}
        error={shouldHideCardDetails || isOnHold}
        variant={variant}
      >

        {cardErrorState.hasError && (
          <StyledCreditCardBlockedWindow
            className={className}
            fullBordered={isOnHold}
            isPlaceholder={isPlaceholder}
          />
        )}

        {cardErrorState.message && (
          <StyledCreditCardTopAlert
            message={cardErrorState.message}
            type='warning'
            showIcon
          />
        )}

        <StyledCreditCardDetails>
          <CardLogo className='card-logo' />

          {shouldHideCardDetails ? (
            <>
              {isTerminated && cardOwnerAndNumber}
              {error && <StyledCreditCardErrorMessage>{t('main:error')}</StyledCreditCardErrorMessage>}
            </>
          ) : (
            <>
              {isPlaceholder ? (
                <StyledCreditCardNumber isPlaceholder={isPlaceholder}>
                  {cardNumber}
                </StyledCreditCardNumber>
              ) : (
                <>
                  {cardOwnerAndNumber}
                  <StyledCreditCardFooter>
                    <StyledCreditCardCVVCode>
                      <span>**/**</span>
                      {t('cvv')}
                    </StyledCreditCardCVVCode>
                    <MasterCardLogo />
                  </StyledCreditCardFooter>
                </>
              )}
            </>
          )}
        </StyledCreditCardDetails>

        {isFrozen && (
          <StyledCreditCardFrozenBackground>
            <StyledCreditCardFrozenMessage>
              <img src={frozenImg} alt='frozen' />
              {t('main:frozen')}
            </StyledCreditCardFrozenMessage>
          </StyledCreditCardFrozenBackground>
        )}

      </StyledCreditCard>

      {actions && (
        <StyledCreditCardBottomAlert
          message={actions}
          type='warning'
        />
      )}

      {enableLimit && !actions && (
        <StyledCreditCardLimit>
          <Progress
            percent={percent}
            size='small'
            showInfo={false}
            status={percent > 100 ? 'exception' : 'success'}
          />
          <StyledCreditCardLimitHelper>
            <span>{used}</span>
            <span>{limit}</span>
          </StyledCreditCardLimitHelper>
        </StyledCreditCardLimit>
      )}

    </StyledCreditCardContainer>
  )
}

CreditCard.propTypes = {
  cardData: PropTypes.shape({
    embossedName: PropTypes.string.isRequired,
    number: PropTypes.string.isRequired,
  }).isRequired,
  isFrozen: PropTypes.bool,
  isOnHold: PropTypes.bool,
  isTerminated: PropTypes.bool,
  hasNoId: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.any,
  limits: PropTypes.shape({
    limit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    used: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  showPlaceholder: PropTypes.bool,
  variant: PropTypes.oneOf(Object.values(cardTypesConstants))
}

export default CreditCard;
